import type {
  ExpressionAst,
  JsonValue,
  SwitchCase,
} from './functional-control-flow';
import { formatFormula, tryFormatFormula } from './formula-format';

/**
 * The Switch case builder's model and its two-way mapping to the backend's
 * constrained expression tree.
 *
 * Humans see fields, plain-word operators and typed values; the tree they
 * produce is exactly what `nodes/logic/switch` evaluates. The `value` root
 * that every path must start with is added here and never shown. Anything
 * the builder's grammar cannot express is shown as a one-line *formula*
 * (`n % 15 == 0`, Phase 8a) the author can edit as text; a tree the formula
 * printer cannot render is kept verbatim as an "advanced" condition so no
 * authored JSON is ever lost.
 */

export const VALUE_ROOT = 'value';

/** A field path relative to the incoming value (no `value` root). */
export type FieldPath = Array<string | number>;

export type ComparisonOperator = 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte';
export type MembershipOperator = 'in' | 'not_in';
export type PresenceOperator = 'exists' | 'not_exists' | 'is_null' | 'not_null';
export type RuleOperator =
  | ComparisonOperator
  | MembershipOperator
  | PresenceOperator;

export const COMPARISON_OPERATORS: ComparisonOperator[] = [
  'eq',
  'ne',
  'lt',
  'lte',
  'gt',
  'gte',
];
export const MEMBERSHIP_OPERATORS: MembershipOperator[] = ['in', 'not_in'];
export const PRESENCE_OPERATORS: PresenceOperator[] = [
  'exists',
  'not_exists',
  'is_null',
  'not_null',
];
export const RULE_OPERATORS: RuleOperator[] = [
  ...COMPARISON_OPERATORS,
  ...MEMBERSHIP_OPERATORS,
  ...PRESENCE_OPERATORS,
];

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  eq: 'equals',
  ne: 'does not equal',
  lt: 'is less than',
  lte: 'is at most',
  gt: 'is greater than',
  gte: 'is at least',
  in: 'is one of',
  not_in: 'is not one of',
  exists: 'exists',
  not_exists: 'does not exist',
  is_null: 'is empty (null)',
  not_null: 'is not empty',
};

export const operatorTakesValue = (op: RuleOperator): boolean =>
  !PRESENCE_OPERATORS.includes(op as PresenceOperator);

export const operatorTakesList = (op: RuleOperator): boolean =>
  MEMBERSHIP_OPERATORS.includes(op as MembershipOperator);

/** Operators whose evaluation reads the field and so can hit a missing path. */
export const operatorCanGuardMissing = (op: RuleOperator): boolean =>
  op !== 'exists' && op !== 'not_exists';

export type Condition =
  | {
      kind: 'rule';
      field: FieldPath;
      op: RuleOperator;
      /** Comparison value, or the list for membership; ignored by presence ops. */
      value: JsonValue;
      /** Wrap in an `exists` guard so a missing field is "no match" not an error. */
      guardMissing: boolean;
    }
  | { kind: 'group'; op: 'and' | 'or'; items: Condition[] }
  | { kind: 'always'; value: boolean }
  /** One line of formula text (Phase 8a); `ast` is what the backend parsed it to. */
  | { kind: 'formula'; text: string; ast: ExpressionAst }
  | { kind: 'advanced'; ast: ExpressionAst };

/** A rule/group/always as a formula the author can keep editing as text. */
export const conditionToFormula = (
  condition: Condition,
  root: string = VALUE_ROOT,
): Extract<Condition, { kind: 'formula' }> => {
  const ast = conditionToAst(condition, root);
  return { kind: 'formula', text: formatFormula(ast, root), ast };
};

export const newRule = (
  field: FieldPath = [],
  op: RuleOperator = 'eq',
): Condition => ({
  kind: 'rule',
  field,
  op,
  value: operatorTakesList(op) ? [] : '',
  guardMissing: false,
});

// --- field paths -----------------------------------------------------------

/** `rating.total` → ['rating','total']; `items.0.name` → ['items',0,'name']. */
export const parseFieldPath = (text: string): FieldPath =>
  text
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment));

export const formatFieldPath = (path: ReadonlyArray<string | number>): string =>
  path.join('.');

type PathAst = Extract<ExpressionAst, { op: 'path' }>;

const pathExpression = (field: FieldPath, root: string): PathAst => ({
  op: 'path',
  path: [root, ...field],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Dotted field paths found in a sample value, breadth first, bounded. */
export const collectFieldSuggestions = (
  sample: unknown,
  maxDepth = 3,
  maxItems = 40,
): string[] => {
  const out: string[] = [];
  const walk = (value: unknown, prefix: FieldPath, depth: number) => {
    if (out.length >= maxItems || depth > maxDepth) return;
    if (isRecord(value)) {
      for (const key of Object.keys(value)) {
        const path = [...prefix, key];
        out.push(formatFieldPath(path));
        walk(value[key], path, depth + 1);
      }
    } else if (Array.isArray(value) && value.length > 0) {
      const path = [...prefix, 0];
      out.push(formatFieldPath(path));
      walk(value[0], path, depth + 1);
    }
  };
  walk(sample, [], 1);
  return out.slice(0, maxItems);
};

/**
 * Offered in the field picker when the upstream value is plain text, a
 * number or a boolean (a classifier's label, a count): the case compares the
 * value itself, which is the empty field path (`["value"]` once rooted).
 */
export const WHOLE_VALUE_SUGGESTION = '(the value itself)';

/** The field picker's text → a field path; the whole-value wording is `[]`. */
export const fieldFromText = (text: string): FieldPath =>
  text.trim() === WHOLE_VALUE_SUGGESTION ? [] : parseFieldPath(text);

/** `collectFieldSuggestions`, plus the value itself when the sample is a scalar. */
export const fieldSuggestionsForSample = (sample: unknown): string[] => {
  const fields = collectFieldSuggestions(sample);
  const scalar =
    typeof sample === 'string' ||
    typeof sample === 'number' ||
    typeof sample === 'boolean';
  return scalar ? [WHOLE_VALUE_SUGGESTION, ...fields] : fields;
};

// --- condition -> tree -----------------------------------------------------

/**
 * `root` is the context key every path starts with: `value` for Switch and
 * Validate, `judge` for a Repeat Until exit condition. It is added here and
 * never shown to the author.
 */
export const conditionToAst = (
  condition: Condition,
  root: string = VALUE_ROOT,
): ExpressionAst => {
  switch (condition.kind) {
    case 'always':
      return { op: 'literal', value: condition.value };
    case 'advanced':
    case 'formula':
      return condition.ast;
    case 'group':
      if (condition.items.length === 0) {
        return { op: 'literal', value: true };
      }
      return {
        op: condition.op,
        args: condition.items.map((item) => conditionToAst(item, root)),
      };
    case 'rule': {
      const path = pathExpression(condition.field, root);
      let core: ExpressionAst;
      switch (condition.op) {
        case 'exists':
          return { op: 'exists', path };
        case 'not_exists':
          return { op: 'not', arg: { op: 'exists', path } };
        case 'is_null':
          core = { op: 'is_null', arg: path };
          break;
        case 'not_null':
          core = { op: 'not', arg: { op: 'is_null', arg: path } };
          break;
        case 'in':
        case 'not_in':
          core = {
            op: condition.op,
            item: path,
            collection: {
              op: 'literal',
              value: Array.isArray(condition.value)
                ? condition.value
                : [condition.value],
            },
          };
          break;
        default:
          core = {
            op: condition.op,
            left: path,
            right: { op: 'literal', value: condition.value },
          };
      }
      if (condition.guardMissing) {
        return { op: 'and', args: [{ op: 'exists', path }, core] };
      }
      return core;
    }
  }
};

// --- tree -> condition -----------------------------------------------------

const fieldFromPathExpression = (
  node: unknown,
  root: string,
): FieldPath | null => {
  if (
    !isRecord(node) ||
    node.op !== 'path' ||
    !Array.isArray(node.path) ||
    node.path[0] !== root
  ) {
    return null;
  }
  const rest = node.path.slice(1);
  if (
    !rest.every(
      (segment) =>
        (typeof segment === 'string' && segment.length > 0) ||
        (typeof segment === 'number' && Number.isInteger(segment)),
    )
  ) {
    return null;
  }
  return rest as FieldPath;
};

const literalValue = (node: unknown): { value: JsonValue } | null =>
  isRecord(node) && node.op === 'literal' && 'value' in node
    ? { value: node.value as JsonValue }
    : null;

/**
 * A literal the rule row can edit: text, number, true/false, null, or a
 * list of those (and of JSON items). A plain object is the one shape the
 * row has no input for, so it stays a custom expression.
 */
const editableLiteral = (node: unknown): { value: JsonValue } | null => {
  const literal = literalValue(node);
  if (!literal) return null;
  return isRecord(literal.value) ? null : literal;
};

const FLIPPED: Record<ComparisonOperator, ComparisonOperator> = {
  eq: 'eq',
  ne: 'ne',
  lt: 'gt',
  lte: 'gte',
  gt: 'lt',
  gte: 'lte',
};

const sameField = (a: FieldPath, b: FieldPath): boolean =>
  a.length === b.length && a.every((segment, i) => segment === b[i]);

/** Parse a bare (unguarded) rule; null when the shape is not one the builder owns. */
const parseBareRule = (ast: unknown, root: string): Condition | null => {
  if (!isRecord(ast) || typeof ast.op !== 'string') return null;
  const op = ast.op;
  if (COMPARISON_OPERATORS.includes(op as ComparisonOperator)) {
    // A comparison against an object literal is kept verbatim (advanced):
    // the row has no input for it. Lists are edited as a list value.
    const left = fieldFromPathExpression(ast.left, root);
    const right = editableLiteral(ast.right);
    if (left && right) {
      return {
        kind: 'rule',
        field: left,
        op: op as ComparisonOperator,
        value: right.value,
        guardMissing: false,
      };
    }
    const flippedField = fieldFromPathExpression(ast.right, root);
    const flippedLiteral = editableLiteral(ast.left);
    if (flippedField && flippedLiteral) {
      return {
        kind: 'rule',
        field: flippedField,
        op: FLIPPED[op as ComparisonOperator],
        value: flippedLiteral.value,
        guardMissing: false,
      };
    }
    return null;
  }
  if (op === 'in' || op === 'not_in') {
    const field = fieldFromPathExpression(ast.item, root);
    const collection = literalValue(ast.collection);
    if (field && collection && Array.isArray(collection.value)) {
      return {
        kind: 'rule',
        field,
        op,
        value: collection.value,
        guardMissing: false,
      };
    }
    return null;
  }
  if (op === 'exists') {
    const field = fieldFromPathExpression(ast.path, root);
    return field
      ? { kind: 'rule', field, op: 'exists', value: null, guardMissing: false }
      : null;
  }
  if (op === 'is_null') {
    const field = fieldFromPathExpression(ast.arg, root);
    return field
      ? { kind: 'rule', field, op: 'is_null', value: null, guardMissing: false }
      : null;
  }
  if (op === 'not' && isRecord(ast.arg)) {
    const inner = parseBareRule(ast.arg, root);
    if (inner?.kind === 'rule' && inner.op === 'exists') {
      return { ...inner, op: 'not_exists' };
    }
    if (inner?.kind === 'rule' && inner.op === 'is_null') {
      return { ...inner, op: 'not_null' };
    }
    return null;
  }
  return null;
};

export const astToCondition = (
  ast: ExpressionAst,
  root: string = VALUE_ROOT,
): Condition => {
  const node: unknown = ast;
  if (!isRecord(node) || typeof node.op !== 'string') {
    return { kind: 'advanced', ast };
  }
  if (node.op === 'literal' && typeof node.value === 'boolean') {
    return { kind: 'always', value: node.value };
  }
  if (node.op === 'and' && Array.isArray(node.args) && node.args.length === 2) {
    // `exists(P) and <rule on P>` is the builder's "missing field = no match" guard.
    const guard = parseBareRule(node.args[0], root);
    const inner = parseBareRule(node.args[1], root);
    if (
      guard?.kind === 'rule' &&
      guard.op === 'exists' &&
      inner?.kind === 'rule' &&
      operatorCanGuardMissing(inner.op) &&
      sameField(guard.field, inner.field)
    ) {
      return { ...inner, guardMissing: true };
    }
  }
  if ((node.op === 'and' || node.op === 'or') && Array.isArray(node.args)) {
    return {
      kind: 'group',
      op: node.op,
      items: (node.args as ExpressionAst[]).map((item) =>
        astToCondition(item, root),
      ),
    };
  }
  const rule = parseBareRule(node, root);
  if (rule) return rule;
  const text = tryFormatFormula(ast, root);
  return text !== null
    ? { kind: 'formula', text, ast }
    : { kind: 'advanced', ast };
};

// --- cases -----------------------------------------------------------------

export interface BuilderCase {
  label: string;
  condition: Condition;
}

export const casesToBuilder = (cases: SwitchCase[]): BuilderCase[] =>
  cases.map((switchCase) => ({
    label: switchCase.label,
    condition: astToCondition(switchCase.when),
  }));

export const builderToCases = (cases: BuilderCase[]): SwitchCase[] =>
  cases.map((entry) => ({
    label: entry.label,
    when: conditionToAst(entry.condition),
  }));

/** True when a tree is all fields/operators/values (no formula or JSON needed). */
export const isBuilderRepresentable = (
  ast: ExpressionAst,
  root: string = VALUE_ROOT,
): boolean => {
  const condition = astToCondition(ast, root);
  const hasAdvanced = (c: Condition): boolean =>
    c.kind === 'advanced' ||
    c.kind === 'formula' ||
    (c.kind === 'group' && c.items.some(hasAdvanced));
  return !hasAdvanced(condition);
};

/** Plain-words summary of a condition, for compact rows and tooltips. */
export const describeCondition = (condition: Condition): string => {
  switch (condition.kind) {
    case 'always':
      return condition.value ? 'always' : 'never';
    case 'advanced':
      return 'custom expression';
    case 'formula':
      return condition.text.trim() || 'empty formula';
    case 'group': {
      const joiner = condition.op === 'and' ? ' and ' : ' or ';
      return condition.items.length === 0
        ? 'always'
        : condition.items.map(describeCondition).join(joiner);
    }
    case 'rule': {
      const field = condition.field.length
        ? formatFieldPath(condition.field)
        : 'the value';
      const label = OPERATOR_LABELS[condition.op];
      if (!operatorTakesValue(condition.op)) return `${field} ${label}`;
      return `${field} ${label} ${JSON.stringify(condition.value)}`;
    }
  }
};

// --- typed value editing ---------------------------------------------------

/** How a comparison value is edited; `list` is a literal list (Phase 2). */
export type ValueKind = 'text' | 'number' | 'boolean' | 'null' | 'list';
/** How each item of a list literal is edited; `json` for mixed or nested items. */
export type ElementKind = 'text' | 'number' | 'boolean' | 'null' | 'json';

export const valueKindOf = (value: JsonValue): ValueKind => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'list';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'text';
};

/** The one kind every item of a list shares, else `json`. Empty → `text`. */
export const elementKindOf = (value: JsonValue): ElementKind => {
  if (!Array.isArray(value) || value.length === 0) return 'text';
  const kinds = new Set(value.map((item) => valueKindOf(item)));
  if (kinds.size !== 1) return 'json';
  const [only] = [...kinds];
  return only === 'list' ? 'json' : only;
};

const splitListText = (text: string): string[] =>
  (text.includes('\n') ? text.split('\n') : text.split(','))
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

/**
 * Parse a list literal typed as comma-separated or one-per-line items.
 * `json` items are parsed as JSON (so nested lists/objects and mixed kinds
 * work); the scalar kinds reuse the typed parsers. `null` when invalid.
 */
export const parseListLiteral = (
  text: string,
  elementKind: ElementKind,
): { value: JsonValue[] } | null => {
  const items = splitListText(text);
  const values: JsonValue[] = [];
  for (const item of items) {
    if (elementKind === 'json') {
      try {
        values.push(JSON.parse(item) as JsonValue);
      } catch {
        return null;
      }
      continue;
    }
    const parsed = parseTypedValue(item, elementKind);
    if (!parsed) return null;
    values.push(parsed.value);
  }
  return { value: values };
};

/** The inverse of `parseListLiteral`: one per line when an item has commas. */
export const formatListLiteral = (
  value: JsonValue,
  elementKind: ElementKind,
): string => {
  if (!Array.isArray(value)) return '';
  const items = value.map((item) =>
    elementKind === 'json'
      ? JSON.stringify(item)
      : typeof item === 'string'
        ? item
        : JSON.stringify(item),
  );
  return items.some((item) => item.includes(','))
    ? items.join('\n')
    : items.join(', ');
};

/** Parse user text into a value of `kind`; null when the text is not valid. */
export const parseTypedValue = (
  text: string,
  kind: ValueKind | ElementKind,
): { value: JsonValue } | null => {
  switch (kind) {
    case 'list':
      return parseListLiteral(text, 'json');
    case 'json':
      try {
        return { value: JSON.parse(text) as JsonValue };
      } catch {
        return null;
      }
    case 'text':
      return { value: text };
    case 'number': {
      const trimmed = text.trim();
      if (trimmed === '') return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? { value: parsed } : null;
    }
    case 'boolean': {
      const normalized = text.trim().toLowerCase();
      if (normalized === 'true') return { value: true };
      if (normalized === 'false') return { value: false };
      return null;
    }
    case 'null':
      return { value: null };
  }
};

/** Parse a comma-separated list, each item typed by `kind`. */
export const parseTypedList = (
  text: string,
  kind: ValueKind | ElementKind,
): { value: JsonValue[] } | null => {
  const items = text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  const values: JsonValue[] = [];
  for (const item of items) {
    const parsed = parseTypedValue(item, kind);
    if (!parsed) return null;
    values.push(parsed.value);
  }
  return { value: values };
};

export const formatTypedList = (value: JsonValue): string =>
  Array.isArray(value)
    ? value
        .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
        .join(', ')
    : '';
